import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { API_URL } from '../services/api';

interface PricingEntry {
    currencyCode: string;
    currencySymbol: string;
    pricePerCredit: number;
    isDefault: boolean;
}

interface CurrencyContextType {
    currentCurrency: string;
    currentSymbol: string;
    prices: PricingEntry[];
    setCurrency: (code: string) => void;
    isLoading: boolean;
    getPriceForCredits: (credits: number) => number;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const CurrencyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [currentCurrency, setCurrencyState] = useState<string>(() => localStorage.getItem('selected_currency') || 'USD');
    const [prices, setPrices] = useState<PricingEntry[]>([]);
    const [isLoading, setIsLoading] = useState(true);



    useEffect(() => {
        const fetchPricing = async () => {
            try {
                const response = await fetch(`${API_URL}/api/credits/pricing`);
                const result = await response.json();
                if (result.success && result.data) {
                    setPrices(result.data.pricing || []);
                }
            } catch (error) {
                console.error('Failed to fetch pricing data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPricing();
    }, [API_URL]);

    const setCurrency = (code: string) => {
        setCurrencyState(code);
        localStorage.setItem('selected_currency', code);
    };

    const currentPricing = prices.find(p => p.currencyCode === currentCurrency);
    const currentSymbol = currentPricing?.currencySymbol || '$';

    // Calculation logic: 
    // 1. If custom price exists for currency, use it.
    // 2. Otherwise we'd need exchange rates (handled on backend for purchase, 
    // but for UI display we can either fetch rates or just show the custom price).
    // The user said: "otherwise they would just be converted ... from the set dollar price"
    const getPriceForCredits = (credits: number) => {
        if (currentPricing) {
            return Number(currentPricing.pricePerCredit) * credits;
        }
        
        // Fallback to USD (Default) if specific currency pricing isn't loaded yet
        const defaultEntry = prices.find(p => p.isDefault) || { pricePerCredit: 0.12 };
        return Number(defaultEntry.pricePerCredit) * credits;
    };

    return (
        <CurrencyContext.Provider value={{ 
            currentCurrency, 
            currentSymbol, 
            prices, 
            setCurrency, 
            isLoading,
            getPriceForCredits 
        }}>
            {children}
        </CurrencyContext.Provider>
    );
};

export const useCurrency = () => {
    const context = useContext(CurrencyContext);
    if (!context) {
        throw new Error('useCurrency must be used within a CurrencyProvider');
    }
    return context;
};
